import React, { FunctionComponent } from 'react';
import { FNIType, STATES } from '@upstart/harmony/lib/legos/constants';
import { DealerLaunchStepProps } from '../types';
import { Dealer } from '../../../types/dealerTypes';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import { getLendingStatus } from '../../../utils/lending';
import { LendingStatus } from '../../../utils/constants';
import { Button } from '../../Input/Button/Button';
import { useTypedSelector } from '../../../redux/store';

const title = (lendingStatus: LendingStatus) => {
  return lendingStatus === LendingStatus.Disabled
    ? 'Next Steps - Upstart Auto Retail Lending'
    : "Great news! You're almost done setting up Upstart Auto Retail Lending";
};

const cardTitle = (lendingStatus: LendingStatus) => {
  return lendingStatus === LendingStatus.Disabled
    ? "We'll let you know when lending is available for your dealership"
    : 'Remember to configure your F&I system later';
};

const paragraph1 = (lendingStatus: LendingStatus) => {
  return lendingStatus === LendingStatus.Disabled ? (
    <p>Thank you for submitting your lending information.</p>
  ) : (
    <p>
      There are more steps you must complete in your F&I system to enable
      Upstart Auto Retail Lending.
      <b> Don’t worry – you don’t need to do this right now.</b>
    </p>
  );
};

const paragraph2 = (fniType: FNIType) => {
  if (fniType === FNIType.DEALERTRACK) {
    const p =
      'When you are done setting up your account here in Launch Pad, then log in to your F&I system Dealertrack and follow the setup guide below. We recommend you download the setup guide and save it to your computer so you can use it later.';
    return (
      <>
        <p>{p}</p>
        <Button
          label="Download Dealertrack Setup Guide"
          variant="minimal"
          onClick={() =>
            window.open(
              'https://prodigy-cdn.upstart.com/launchpad/FNI/Dealertrack+2021+Finance+Services+Set-up+Guide.pdf',
              '_blank',
            )
          }
        />
      </>
    );
  }

  if (fniType === FNIType.ROUTEONE) {
    const p =
      'When you are done setting up your account here in Launch Pad, then log in to your F&I system RouteOne and follow the setup guide below. We recommend you download the setup guide and save it to your computer so you can use it later.';
    return (
      <>
        <p>{p}</p>
        <Button
          label="Download RouteOne Setup Guide"
          variant="minimal"
          onClick={() =>
            window.open(
              'https://prodigy-cdn.upstart.com/launchpad/FNI/RouteOne+ORS+Setup+Guide.pptx',
              '_blank',
            )
          }
        />
      </>
    );
  }

  const p =
    "Upstart Auto Retail Lending is available only at this time for F&I systems RouteOne and Dealertrack. Don't worry, we'll let you know when it's available for your F&I system.";
  return <p>{p}</p>;
};

const paragraph3 = (lendingStatus: LendingStatus, state: STATES) => {
  return (
    lendingStatus === LendingStatus.StateDisabled && (
      <p>
        {`We're launching lending in more states each month and will let you know soon when it's available in 
      ${state || 'your state'}.`}
      </p>
    )
  );
};

const paragraph4 = (
  <p>
    Next, click continue below to finish setting up your account in Launch Pad.
  </p>
);

export const DealerLaunchLendingNextSteps: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({ defaultValues: dealer });
  const globalConfigurationStore = useTypedSelector(
    (state) => state.globalConfigurations,
  );
  const { globalConfigurations } = globalConfigurationStore;
  const lendingStatus = getLendingStatus(dealer, globalConfigurations);
  const fniType = dealer.fniType as FNIType;
  const state = STATES[dealer.state as keyof typeof STATES];

  return (
    <DealerLaunchFormCardLayout
      form={form}
      title={title(lendingStatus)}
      cardTitle={cardTitle(lendingStatus)}
      prevStep={prevStep}
      onFormSubmit={nextStep}
      contanctInfo
    >
      {paragraph1(lendingStatus)}
      {paragraph2(fniType)}
      {paragraph3(lendingStatus, state)}
      {paragraph4}
    </DealerLaunchFormCardLayout>
  );
};
